// Pure planning helpers for the gated local ASR engine boot (TT-1313 / CR-10679).
//
// The WASM engine boot (worker spawn + wasm instantiate + model download-or-IDBFS-restore
// + Kaldi model construction) posts no progress, so the loading bar for that phase is an
// ESTIMATE: it fills linearly over `estimateMs`, jumps to done if the boot finishes early,
// and holds at ASR_BOOT_PROGRESS_CEILING if it runs long. A hard `timeoutMs` bounds the
// wait regardless, after which App falls back to server ASR so a student is never
// stranded. Both numbers depend on whether the model is already extracted in IDBFS:
//
//   warm    IDBFS restore + model construction. ~1-3 s on a laptop, longer on a 4 GB
//           Chromebook; the 10 s estimate is deliberately generous so the bar usually
//           finishes early rather than parking at the ceiling.
//   cold    the worker must first download the 22.8 MB acoustic archive. Sized from the
//           link throughput App just measured while fetching the small precache assets,
//           plus the warm boot on top. The TIMEOUT stays the fixed cold budget (the same
//           5-minute scale the precache download used to have): a cold device that times
//           out has thrown away nothing — the worker keeps downloading and extracts into
//           IDBFS when it lands, so the NEXT session is warm — but the student has already
//           been sent to server ASR for this one, so the budget is what decides how long a
//           slow link may hold the loading screen.
//   unknown the probe could not answer (no IndexedDB API, open hung). Warm estimate for
//           movement, cold timeout for safety — shortening the budget on a guess would
//           deny a genuinely cold device local ASR forever.
//
// Everything here is arithmetic on inputs App supplies, so it is unit-tested in
// isolation; App owns the ticker, the timeout race, and the fallback.

export const LOCAL_ASR_MODEL_PRESENCE = Object.freeze({
  WARM: 'warm',
  COLD: 'cold',
  UNKNOWN: 'unknown',
});

/** Linear-fill duration for a warm boot. Generous on purpose (see header). */
export const ASR_BOOT_WARM_ESTIMATE_MS = 10000;
/** Hard bound for a warm boot before falling back to server ASR. Matches the fail-fast
 * bound voskAsr already uses when the IDBFS open is known to be blocked. */
export const ASR_BOOT_WARM_TIMEOUT_MS = 45000;
/** Hard bound for a cold boot (download + extract + construct). Same scale as the old
 * precache overall timeout; see header for why the timeout is a budget, not a guess. */
export const ASR_BOOT_COLD_TIMEOUT_MS = 300000;
/** The student's wait on the loading gate for ONE ATTEMPT, download + boot, measured from
 * that attempt's `preload.started`. Equal to the old single precache budget for the whole
 * 28 MB: A2 moved the 22.8 MB into the boot phase, so without this the two 5-minute bounds
 * would be additive. boundBootTimeoutToGateBudget shrinks the boot's timeout by what the
 * download already spent, never below the warm bound. Deliberately PER ATTEMPT, not per
 * session: a manual retry follows a failed download, and a session-level budget would
 * leave that retry's cold boot only the 45 s floor — making the retry pointless. */
export const ASR_GATE_TOTAL_BUDGET_MS = 300000;
/** Clamp on the cold ESTIMATE so one odd throughput sample cannot make the bar absurd. */
export const ASR_BOOT_COLD_ESTIMATE_MIN_MS = 20000;
export const ASR_BOOT_COLD_ESTIMATE_MAX_MS = 180000;
/** Assumed link when the precache fetched too little to measure (fully warm Cache
 * Storage but cold IDBFS — rare, e.g. a partial eviction). ~1 MB/s is a mid-range school
 * Wi-Fi figure; being wrong here only mis-shapes the bar, never the timeout. */
export const ASR_BOOT_DEFAULT_THROUGHPUT_BYTES_PER_SEC = 1_000_000;

/** Share of the composite bar the (real, byte-weighted) precache download owns. */
export const ASR_DOWNLOAD_PROGRESS_SHARE = 0.6;
/** Where the estimated boot slice parks while the boot runs long. Never 1: reaching 100%
 * is reserved for the boot actually resolving. */
export const ASR_BOOT_PROGRESS_CEILING = 0.98;

const clamp01 = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n >= 1 ? 1 : n;
};

/**
 * Plans the estimate and the hard bound for one engine boot.
 * @param {{presence?: string, modelBytes?: number, throughputBytesPerSec?: number|null}} input
 *   `presence` is one of LOCAL_ASR_MODEL_PRESENCE (anything else reads as unknown);
 *   `modelBytes` the acoustic archive size the worker must fetch when cold;
 *   `throughputBytesPerSec` the link sample from the precache download, or null/NaN/<=0
 *   when there was not enough traffic to measure.
 * @returns {{presence: string, estimateMs: number, timeoutMs: number, throughputBytesPerSec: number|null}}
 */
export function planLocalAsrBoot({ presence, modelBytes, throughputBytesPerSec } = {}) {
  const known = Object.values(LOCAL_ASR_MODEL_PRESENCE).includes(presence)
    ? presence
    : LOCAL_ASR_MODEL_PRESENCE.UNKNOWN;
  const sample = Number.isFinite(throughputBytesPerSec) && throughputBytesPerSec > 0
    ? throughputBytesPerSec
    : null;

  if (known === LOCAL_ASR_MODEL_PRESENCE.WARM) {
    return {
      presence: known,
      estimateMs: ASR_BOOT_WARM_ESTIMATE_MS,
      timeoutMs: ASR_BOOT_WARM_TIMEOUT_MS,
      throughputBytesPerSec: null,
    };
  }

  if (known === LOCAL_ASR_MODEL_PRESENCE.COLD) {
    const bytes = Number.isFinite(modelBytes) && modelBytes > 0 ? modelBytes : 0;
    const rate = sample || ASR_BOOT_DEFAULT_THROUGHPUT_BYTES_PER_SEC;
    const downloadMs = Math.round((bytes / rate) * 1000);
    const estimateMs = Math.min(
      ASR_BOOT_COLD_ESTIMATE_MAX_MS,
      Math.max(ASR_BOOT_COLD_ESTIMATE_MIN_MS, downloadMs + ASR_BOOT_WARM_ESTIMATE_MS),
    );
    return {
      presence: known,
      estimateMs,
      timeoutMs: ASR_BOOT_COLD_TIMEOUT_MS,
      throughputBytesPerSec: sample,
    };
  }

  return {
    presence: LOCAL_ASR_MODEL_PRESENCE.UNKNOWN,
    estimateMs: ASR_BOOT_WARM_ESTIMATE_MS,
    timeoutMs: ASR_BOOT_COLD_TIMEOUT_MS,
    throughputBytesPerSec: sample,
  };
}

/**
 * Bounds a planned boot timeout by what is left of the total gate budget.
 * @param {{timeoutMs: number, gateElapsedMs?: number}} input `gateElapsedMs` is the time
 *   already spent on the gate (download settled minus preload started).
 * @returns {number} min(timeoutMs, max(warm bound, budget − elapsed))
 */
export function boundBootTimeoutToGateBudget({ timeoutMs, gateElapsedMs } = {}) {
  const elapsed = Number.isFinite(gateElapsedMs) && gateElapsedMs > 0 ? gateElapsedMs : 0;
  const remaining = Math.max(ASR_BOOT_WARM_TIMEOUT_MS, ASR_GATE_TOTAL_BUDGET_MS - elapsed);
  return Math.min(timeoutMs, remaining);
}

/**
 * How far the estimated boot slice has filled: linear over the estimate, held at 1 past it.
 * @param {number} elapsedMs
 * @param {number} estimateMs
 * @returns {number} 0..1
 */
export function estimatedBootFraction(elapsedMs, estimateMs) {
  if (!(estimateMs > 0)) return 1;
  return clamp01(elapsedMs / estimateMs);
}

/**
 * The single loading-bar value: the real download fills its share, then the estimated
 * boot fills the rest up to the ceiling. Exactly 1 only when `done`.
 * @param {{downloadFraction: number, bootFraction: number, done?: boolean}} input
 * @returns {number} 0..1
 */
export function compositeAsrLoadProgress({ downloadFraction, bootFraction, done = false }) {
  if (done) return 1;
  const download = clamp01(downloadFraction) * ASR_DOWNLOAD_PROGRESS_SHARE;
  const boot = clamp01(bootFraction) * (ASR_BOOT_PROGRESS_CEILING - ASR_DOWNLOAD_PROGRESS_SHARE);
  return Math.min(ASR_BOOT_PROGRESS_CEILING, download + boot);
}
